/**
 * Simplified Facebook Pixel Service for tracking recruiter funnel events
 * Handles event mapping and tracking with proper error handling and type safety
 */

/**
 * Facebook Pixel function interface for better type safety
 */
interface FacebookPixelFunction {
  (
    command: "trackCustom",
    eventName: string,
    parameters?: Record<string, unknown>
  ): void;
  (command: "init", pixelId: string): void;
  (command: "track", eventName: string): void;
}

/**
 * Facebook Pixel function interface extending the global Window object
 */
declare global {
  interface Window {
    fbq?: FacebookPixelFunction;
  }
}

/**
 * Interface for Facebook Pixel event mapping for better type safety and extensibility
 */
interface FacebookPixelEventMapping {
  readonly [key: string]: string;
}

/**
 * Facebook Pixel event mapping - maps internal events to Facebook Pixel events
 * Using readonly to prevent accidental mutations
 */
export const FB_PIXEL_EVENT_MAPPING: FacebookPixelEventMapping = {
  OTP_VERIFICATION_SUCCESS: "AddToCart",
  JOB_POST_SUCCESS: "SubmitApplication",
  JOB_POST_SUCCESS_NEW_USER: "SubmitApplication",
  RECRUITER_PROFILE_CREATE: "CompleteRegistration",
  DOC_VERIFICATION_UPLOAD_SUCCESS: "Donate"
} as const;

/**
 * Get Facebook Pixel event name from internal event name
 * @param internalEventName - Internal event name from LogService
 * @returns Facebook Pixel event name or null if not mapped
 */
export const getFacebookPixelEventName = (
  internalEventName: string
): string | null => {
  return FB_PIXEL_EVENT_MAPPING[internalEventName] ?? null;
};

/**
 * Facebook Pixel Service class for managing pixel tracking functionality
 * Implements singleton pattern for consistent tracking across the application
 */
class FacebookPixelService {
  private static instance: FacebookPixelService | null = null;

  /**
   * Private constructor to enforce singleton pattern
   */
  private constructor() {}

  /**
   * Get singleton instance of FacebookPixelService
   * @returns The singleton instance
   */
  public static getInstance(): FacebookPixelService {
    if (!FacebookPixelService.instance) {
      FacebookPixelService.instance = new FacebookPixelService();
    }
    return FacebookPixelService.instance;
  }

  /**
   * Check if Facebook Pixel is enabled based on environment and availability
   * @returns True if Facebook Pixel is available and enabled
   */
  private isEnabled(): boolean {
    const hasWindow = typeof window !== "undefined";
    const hasFbq = hasWindow && !!window.fbq;
    const isProduction = process.env.BASE_URL === "https://api.jobhai.com";

    return hasWindow && hasFbq && isProduction;
  }

  /**
   * Generic function to track Facebook Pixel events with enhanced error handling
   * @param eventName - Name of the Facebook Pixel event
   * @throws Will log error to console if tracking fails
   */
  public trackEvent(eventName: string): void {
    if (!eventName?.trim()) {
      return;
    }

    if (!this.isEnabled()) {
      return;
    }

    try {
      if (window.fbq) {
        window.fbq("track", eventName);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      console.error(`[FB Pixel] Error tracking ${eventName}:`, {
        error: errorMessage,
        eventName,
        timestamp: new Date().toISOString()
      });
    }
  }
}

/**
 * Export singleton instance using proper singleton pattern
 */
const facebookPixelService = FacebookPixelService.getInstance();
export default facebookPixelService;

/**
 * Fire Facebook Pixel event based on internal event name with enhanced validation
 * @param internalEventName - Internal event name from LogService
 */
export const fireFacebookPixelEvent = (internalEventName: string): void => {
  if (!internalEventName?.trim()) {
    return;
  }

  const fbEventName = getFacebookPixelEventName(internalEventName);
  if (fbEventName) {
    facebookPixelService.trackEvent(fbEventName);
  }
};
